import { SpamStatus, Transaction, TransactionTypeValue } from '../../../services/transaction.types';

const NON_SPAMMABLE_TYPES = [
    TransactionTypeValue.Trade,
    TransactionTypeValue.OutProvideLiquidity,
    TransactionTypeValue.OutReturnLpToken,
    TransactionTypeValue.InLpIncome,
    TransactionTypeValue.InRemoveLiquidity,
    TransactionTypeValue.InReceiveLpToken,
    TransactionTypeValue.SwapWrapNonTaxable
];

export const canMarkAsSpam = (transaction: Transaction): boolean => {
    if (NON_SPAMMABLE_TYPES.includes(transaction.typeId)) {
        return false;
    }

    if (!transaction.contractAddress || transaction.contractAddress.length === 0) {
        return false;
    }

    return transaction.platformIdentifier.length > 0;
};

export const getSpamParams = (
    transaction: Transaction
): { contractAddress: string; platformIdentifier: string; coin: string } | null => {
    if (!canMarkAsSpam(transaction)) {
        return null;
    }

    if (transaction.type.in && transaction.buyCoin && transaction.contractAddress) {
        return {
            contractAddress: transaction.contractAddress,
            platformIdentifier: transaction.platformIdentifier,
            coin: transaction.buyCoin,
        };
    }

    if (transaction.type.out && transaction.sellCoin && transaction.contractAddress) {
        return {
            contractAddress: transaction.contractAddress,
            platformIdentifier: transaction.platformIdentifier,
            coin: transaction.sellCoin,
        };
    }

    return null;
};

export const processSpamTransactions = (
    transactions: Transaction[]
): Array<{ contractAddress: string; platformIdentifier: string; coin: string }> => {
    const spamParams: Array<{ contractAddress: string; platformIdentifier: string; coin: string }> = [];

    transactions.forEach((transaction) => {
        const params = getSpamParams(transaction);
        if (params) {
            spamParams.push(params);
        }
    });

    return spamParams;
};

export const canMarkAsNotSpam = (transaction: Transaction): boolean => {
    if (NON_SPAMMABLE_TYPES.includes(transaction.typeId)) {
        return false;
    }

    if (!transaction.contractAddress || transaction.contractAddress.length === 0) {
        return false;
    }

    return (
        transaction.platformIdentifier.length > 0 &&
        transaction.spamCoin === SpamStatus.Spam &&
        transaction.blacklistId !== undefined
    );
};

export const getNotSpamParams = (
    transaction: Transaction
): { contractAddress: string; platformIdentifier: string; blacklistId: number } | null => {
    if (!canMarkAsNotSpam(transaction)) {
        return null;
    }

    if (transaction.platformIdentifier && transaction.contractAddress && transaction.blacklistId) {
        return {
            contractAddress: transaction.contractAddress,
            platformIdentifier: transaction.platformIdentifier,
            blacklistId: transaction.blacklistId,
        };
    }

    return null;
};

export const processNotSpamTransactions = (
    transactions: Transaction[]
): Array<{ contractAddress: string; platformIdentifier: string; blacklistId: number }> => {
    const spamParams: Array<{ contractAddress: string; platformIdentifier: string; blacklistId: number }> = [];

    transactions.forEach((transaction) => {
        const params = getNotSpamParams(transaction);
        if (params) {
            spamParams.push(params);
        }
    });

    return spamParams;
};

export const canMarkAsNonTaxable = (transaction: Transaction): boolean => {
    const invalidTransactionTypes = [
        TransactionTypeValue.InDeposit,
        TransactionTypeValue.OutWithdrawal,
        TransactionTypeValue.InIncomeNonTaxable,
        TransactionTypeValue.InAirdropNonTaxable,
        TransactionTypeValue.InOtherIncomeNonTaxable,
        TransactionTypeValue.OutExpenseNonTaxable,
        TransactionTypeValue.SwapWrapNonTaxable,
    ];

    return !invalidTransactionTypes.includes(transaction.typeId);
};

export const getNonTaxableParams = (
    transaction: Transaction
): { id: number; targetTypeId: TransactionTypeValue } | null => {
    if (!canMarkAsNonTaxable(transaction) || transaction.id === undefined) {
        return null;
    }

    let targetTypeId: TransactionTypeValue;

    if (transaction.type.in && !transaction.type.out) {
        if (transaction.typeId === TransactionTypeValue.InIncome) {
            targetTypeId = TransactionTypeValue.InIncomeNonTaxable;
        } else if (transaction.typeId === TransactionTypeValue.InAirdrop) {
            targetTypeId = TransactionTypeValue.InAirdropNonTaxable;
        } else {
            targetTypeId = TransactionTypeValue.InOtherIncomeNonTaxable;
        }
    } else if (transaction.type.out && !transaction.type.in) {
        targetTypeId = TransactionTypeValue.OutExpenseNonTaxable;
    } else if (transaction.typeId === TransactionTypeValue.Trade) {
        targetTypeId = TransactionTypeValue.SwapWrapNonTaxable;
    } else {
        return null;
    }

    return { id: transaction.id, targetTypeId };
};

export const processNonTaxableTransactions = (
    transactions: Transaction[]
): Array<{ id: number; targetTypeId: TransactionTypeValue }> => {
    const nonTaxableParams: Array<{ id: number; targetTypeId: TransactionTypeValue }> = [];

    transactions.forEach((transaction) => {
        const params = getNonTaxableParams(transaction);
        if (params) {
            nonTaxableParams.push(params);
        }
    });

    return nonTaxableParams;
};

export const canMarkAsTaxable = (transaction: Transaction): boolean => {
    const validTransactionTypes = [
        TransactionTypeValue.InIncomeNonTaxable,
        TransactionTypeValue.InAirdropNonTaxable,
        TransactionTypeValue.InOtherIncomeNonTaxable,
        TransactionTypeValue.OutExpenseNonTaxable,
        TransactionTypeValue.SwapWrapNonTaxable,
    ];

    return validTransactionTypes.includes(transaction.typeId);
};

export const getTaxableParams = (
    transaction: Transaction
): { id: number; targetTypeId: TransactionTypeValue } | null => {
    if (!canMarkAsTaxable(transaction) || transaction.id === undefined) {
        return null;
    }

    let targetTypeId: TransactionTypeValue;

    if (transaction.typeId === TransactionTypeValue.InIncomeNonTaxable) {
        targetTypeId = TransactionTypeValue.InIncome;
    } else if (transaction.typeId === TransactionTypeValue.InAirdropNonTaxable) {
        targetTypeId = TransactionTypeValue.InAirdrop;
    } else if (transaction.typeId === TransactionTypeValue.InOtherIncomeNonTaxable) {
        targetTypeId = TransactionTypeValue.InIncome;
    } else if (transaction.typeId === TransactionTypeValue.OutExpenseNonTaxable) {
        targetTypeId = TransactionTypeValue.OutSpend;
    } else if (transaction.typeId === TransactionTypeValue.SwapWrapNonTaxable) {
        targetTypeId = TransactionTypeValue.Trade;
    } else {
        return null;
    }

    return { id: transaction.id, targetTypeId };
};

export const processTaxableTransactions = (
    transactions: Transaction[]
): Array<{ id: number; targetTypeId: TransactionTypeValue }> => {
    const nonTaxableParams: Array<{ id: number; targetTypeId: TransactionTypeValue }> = [];

    transactions.forEach((transaction) => {
        const params = getTaxableParams(transaction);
        if (params) {
            nonTaxableParams.push(params);
        }
    });

    return nonTaxableParams;
};

export const canBulkMarkAsSpam = (transactions: Transaction[]): boolean => {
    return transactions.every((transaction) => {
        return transaction.spamCoin !== SpamStatus.Spam && canMarkAsSpam(transaction);
    });
};

export const canBulkMarkAsNotSpam = (transactions: Transaction[]): boolean => {
    return transactions.every((transaction) => {
        return transaction.spamCoin === SpamStatus.Spam && canMarkAsNotSpam(transaction);
    });
};

export const canBulkMarkAsNonTaxable = (transactions: Transaction[]): boolean => {
    return transactions.every((tx) => canMarkAsNonTaxable(tx));
};

export const canBulkMarkAsTaxable = (transactions: Transaction[]): boolean => {
    return transactions.every((tx) => canMarkAsTaxable(tx));
};
